import { useEffect, useRef, useState } from "react";
import { Send, Loader2, X, MessageCircle } from "lucide-react";
import { sendChat } from "../lib/api";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { T } from "@/locales/tr";

const PAPICOO_AVATAR = `${process.env.PUBLIC_URL || ""}/papicoo-avatar.png`;

function MiniBubble({ role, content }) {
  const isUser = role === "user";
  return (
    <div className={`flex gap-2 ${isUser ? "justify-end" : "justify-start"} fade-in-up`}>
      {!isUser && (
        <img src={PAPICOO_AVATAR} alt={T("Papicoo")} className="w-7 h-7 rounded-full object-cover border border-[color:var(--papicoo-gold)] shrink-0 mt-1" />
      )}
      <div
        className={`max-w-[82%] px-3.5 py-2 rounded-2xl whitespace-pre-wrap break-words [overflow-wrap:anywhere] leading-relaxed text-[13px] ${
          isUser ? "papicoo-bubble-user rounded-br-sm" : "papicoo-bubble-papi rounded-bl-sm"
        }`}
      >
        {content}
      </div>
    </div>
  );
}

export default function FloatingPapicoo() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scroller = useRef(null);

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: "user", content: text }]);
    setLoading(true);
    try {
      const data = await sendChat({ session_id: sessionId, message: text });
      setSessionId(data.session_id);
      setMessages((m) => {
        const cleaned = m.filter((x) => !String(x.id).startsWith("tmp-"));
        return [...cleaned, data.user_message, data.papicoo_message];
      });
    } catch (err) {
      const st = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const msg = st === 429 && detail === "CHAT_DAILY_LIMIT"
        ? T("Tu as atteint la limite de 20 messages pour aujourd'hui. Reviens demain, inch'Allah.")
        : st === 429
          ? T("Doucement — trop de messages d'affilée. Réessaie dans une minute.")
          : st === 402
            ? T("Les questions offertes sont épuisées pour le moment.")
            : st === 502 || st === 503
              ? T("Le service de réponse est momentanément indisponible. Réessaie plus tard.")
              : T("Salaam — petit silence côté serveur. Réessaie dans un instant.");
      setMessages((m) => [...m, { id: `err-${Date.now()}`, role: "papicoo", content: msg }]);
    } finally { setLoading(false); }
  };

  return (
    <>
      <button
        data-testid="floating-papicoo-btn"
        onClick={() => setOpen(true)}
        aria-label={T("Ouvrir Papicoo")}
        className="fixed bottom-5 right-5 z-50 group flex items-center gap-2 pl-1.5 pr-4 py-1.5 rounded-full bg-[color:var(--papicoo-ink)] text-[color:var(--papicoo-sand)] shadow-xl hover:shadow-2xl transition-all hover:-translate-y-0.5"
      >
        <span className="relative inline-flex">
          <img src={PAPICOO_AVATAR} alt={T("Papicoo")} className="w-10 h-10 rounded-full object-cover border-2 border-[color:var(--papicoo-gold)]" />
          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-400 border-2 border-[color:var(--papicoo-ink)]" />
        </span>
        <span className="text-sm font-medium">{T("Salaam")}</span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          data-testid="floating-papicoo-panel"
          className="w-full sm:w-[420px] p-0 bg-[color:var(--papicoo-sand)] border-l border-[color:var(--papicoo-border)] flex flex-col"
        >
          <VisuallyHidden>
            <SheetTitle>{T("Papicoo — Chat rapide")}</SheetTitle>
            <SheetDescription>{T("Échange direct avec Papicoo en quelques mots.")}</SheetDescription>
          </VisuallyHidden>

          <div className="flex items-center gap-3 px-4 py-3 border-b border-[color:var(--papicoo-border)] bg-[color:var(--papicoo-surface)]">
            <img src={PAPICOO_AVATAR} alt={T("Papicoo")} className="w-10 h-10 rounded-full object-cover border border-[color:var(--papicoo-gold)]" />
            <div className="leading-tight flex-1">
              <div className="font-display text-lg text-[color:var(--papicoo-ink)]">{T("Salaam — c'est Papicoo")}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)]">{T("Toujours à côté de toi")}</div>
            </div>
            <button onClick={() => setOpen(false)} aria-label={T("Fermer")} className="w-8 h-8 inline-flex items-center justify-center rounded-full hover:bg-[color:var(--papicoo-sand-deep)]">
              <X size={16} />
            </button>
          </div>

          <div ref={scroller} className="flex-1 overflow-y-auto papicoo-chat-bg p-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center py-6">
                <img src={PAPICOO_AVATAR} alt={T("Papicoo")} className="w-16 h-16 rounded-full object-cover mx-auto border-2 border-[color:var(--papicoo-gold)]" />
                <div className="font-display text-xl text-[color:var(--papicoo-ink)] mt-3">{T("Salaam.")}</div>
                <p className="text-xs text-[color:var(--papicoo-muted)] mt-1 italic px-3">{T("Pose ta question — foi, business, créativité, vie. Je suis là.")}</p>
                <div className="mt-4 grid gap-2">
                  {[
                    "Un verset pour démarrer la journée ?",
                    "Comment lancer mon projet halâl ?",
                    "Décode-moi ce message bizarre",
                  ].map((s, i) => (
                    <button key={s} data-testid={`floating-starter-${i}`} onClick={() => setInput(T(s))} className="papicoo-card text-left px-3 py-2 text-xs hover:-translate-y-0.5 transition-transform">
                      {T(s)}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => <MiniBubble key={m.id} role={m.role} content={m.content} />)}
            {loading && (
              <div className="flex items-center gap-2 text-[color:var(--papicoo-muted)] text-xs italic">
                <Loader2 size={14} className="animate-spin" /> {T("Papicoo réfléchit…")}
              </div>
            )}
          </div>

          <form onSubmit={submit} className="border-t border-[color:var(--papicoo-border)] p-2 flex gap-2 bg-[color:var(--papicoo-surface)]">
            <input
              data-testid="floating-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={T("Écris à Papicoo…")}
              className="flex-1 min-w-0 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-full px-4 py-2.5 outline-none focus:ring-2 focus:ring-[color:var(--papicoo-ink)]/30 text-sm"
            />
            <button data-testid="floating-send" type="submit" disabled={loading || !input.trim()} className="shrink-0 inline-flex items-center px-4 py-2.5 rounded-full papicoo-btn-primary disabled:opacity-50">
              <Send size={14} />
            </button>
          </form>
        </SheetContent>
      </Sheet>
    </>
  );
}
